/**
 * Sortable, selectable table on the design system's `.dac-grid` styles.
 *
 * Generic over the row type so the grid stays unaware of what a document is;
 * columns supply their own renderers.
 */
import type { ReactNode } from "react";

import { Icon } from "./primitives";

export interface Column<T> {
  key: string;
  label: string;
  width?: number;
  align?: "left" | "right";
  mono?: boolean;
  /** Extra class on the column's header and cells - how a column is hidden at narrow widths. */
  className?: string;
  sortable?: boolean;
  render?: (row: T) => ReactNode;
  /** Value used for sorting when `render` returns markup. */
  sortValue?: (row: T) => string | number;
}

export interface SortState {
  key: string;
  dir: "asc" | "desc";
}

interface DataGridProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  selected?: string[];
  onToggleRow?: (key: string) => void;
  onToggleAll?: () => void;
  sort?: SortState;
  onSort?: (key: string) => void;
  dense?: boolean;
  onRowClick?: (row: T) => void;
}

export function DataGrid<T>({
  columns,
  rows,
  rowKey,
  selected,
  onToggleRow,
  onToggleAll,
  sort,
  onSort,
  dense,
  onRowClick,
}: DataGridProps<T>) {
  const selectable = Boolean(onToggleRow);
  const allSelected = selectable && rows.length > 0 && selected?.length === rows.length;
  const someSelected = selectable && (selected?.length ?? 0) > 0 && !allSelected;

  return (
    <table className={`dac-grid ${dense ? "dac-grid--dense" : ""}`.trim()}>
      <thead>
        <tr>
          {selectable && (
            <th className="dac-grid__check">
              <span
                className="dac-check"
                role="checkbox"
                aria-checked={allSelected ? "true" : someSelected ? "mixed" : "false"}
                aria-label="Select all documents"
                tabIndex={0}
                onClick={onToggleAll}
              >
                <span
                  className={`dac-check__box ${
                    allSelected ? "dac-check__box--on" : someSelected ? "dac-check__box--mixed" : ""
                  }`.trim()}
                />
              </span>
            </th>
          )}
          {columns.map((column) => {
            const sorted = sort?.key === column.key;
            return (
              <th
                key={column.key}
                className={[sorted ? "is-sorted" : "", column.className ?? ""].filter(Boolean).join(" ") || undefined}
                style={{
                  width: column.width,
                  textAlign: column.align === "right" ? "right" : undefined,
                  cursor: column.sortable === false ? "default" : undefined,
                }}
                onClick={column.sortable === false ? undefined : () => onSort?.(column.key)}
              >
                <span className="dac-sort">
                  {column.label}
                  {column.sortable !== false && column.label && (
                    <span className="dac-sort__arrow dac-icon">
                      {sorted && sort?.dir === "desc" ? "arrow_downward" : "arrow_upward"}
                    </span>
                  )}
                </span>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const key = rowKey(row);
          const isSelected = selected?.includes(key) ?? false;
          return (
            <tr
              key={key}
              className={isSelected ? "is-selected" : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{ cursor: onRowClick ? "pointer" : undefined }}
            >
              {selectable && (
                <td className="dac-grid__check" onClick={(event) => event.stopPropagation()}>
                  <span
                    className="dac-check"
                    role="checkbox"
                    aria-checked={isSelected}
                    aria-label="Select document"
                    tabIndex={0}
                    onClick={() => onToggleRow?.(key)}
                  >
                    <span className={`dac-check__box ${isSelected ? "dac-check__box--on" : ""}`.trim()} />
                  </span>
                </td>
              )}
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={[
                    column.align === "right" ? "dac-grid__num" : "",
                    column.mono ? "dac-grid__mono" : "",
                    column.className ?? "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {column.render ? column.render(row) : String((row as Record<string, unknown>)[column.key] ?? "")}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Reorder rows for the active sort, leaving the input untouched. */
export function sortRows<T>(rows: T[], columns: Column<T>[], sort: SortState | undefined): T[] {
  if (!sort) return rows;
  const column = columns.find((candidate) => candidate.key === sort.key);
  if (!column) return rows;

  const value = (row: T): string | number =>
    column.sortValue
      ? column.sortValue(row)
      : ((row as Record<string, unknown>)[column.key] as string | number) ?? "";

  return [...rows].sort((left, right) => {
    const a = value(left);
    const b = value(right);
    const order = a < b ? -1 : a > b ? 1 : 0;
    return sort.dir === "desc" ? -order : order;
  });
}

/** The empty-state block shown in place of a grid. */
export function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon: string;
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty__card">
        <Icon name={icon} size={36} className="empty__icon" />
        <div className="empty__title">{title}</div>
        <div className="empty__text">{text}</div>
        {action}
      </div>
    </div>
  );
}
